import { ApiError, http } from '@/shared/api/client';
import type { MgmParticipantView } from '@/features/member-get-member/model/types';

/**
 * Área do participante no BFF (`/bff/public/participant/*`). A sessão é um
 * cookie httpOnly do próprio BFF, criado depois do código do WhatsApp: o
 * browser nunca manda CPF nem serial nas rotas `/me`.
 */

const PREFIX = '/bff/public/participant';

export interface NotificationChannels {
  email: boolean;
  sms: boolean;
  whatsapp: boolean;
  push: boolean;
  webpush: boolean;
}

export interface ParticipantProfile {
  name: string;
  email: string;
  /** `(DD) 9XXXX-XXXX`, como o legado grava. */
  phone: string;
  /** `dd/mm/aaaa`. */
  birthday: string;
  /** M | F | O | P. */
  gender: string;
  notifications: NotificationChannels;
  /** Campos extras do cadastro da loja + estado/cidade, como texto (checkbox = valores com `|`). */
  fields: Record<string, string>;
}

/** Subconjunto do passbook usado para rotular o histórico (programa, moeda, validade). */
export interface ParticipantProgram {
  hasPoints?: boolean;
  hasCashback?: boolean;
  hasTransaction?: boolean;
  hasGiftcard?: boolean;
  maxInputValidation?: number;
  hasIN?: boolean;
  hasPT?: boolean;
  hasES?: boolean;
  hasEN?: boolean;
  hasExpire?: boolean;
  hasExpireStamps?: boolean;
  expireDays?: number;
}

export interface ParticipantTierNext {
  label: string;
  remaining: number;
}

/** Nível de fidelidade atual (`hasTiers`). Ausente/null = loja sem programa de tiers. */
export interface ParticipantTier {
  level: string;
  label: string;
  color?: string;
  backgroundColor?: string;
  foregroundColor?: string;
  labelColor?: string;
  benefits?: string;
  /** 1-based na escada. */
  rank: number;
  count: number;
  /** 0–1 até o próximo nível; 1 no topo. */
  progress: number;
  next: ParticipantTierNext | null;
}

export interface ParticipantMe {
  firstName: string;
  /** CPF só com dígitos — o da sessão. */
  uniqueId: string;
  /** Número do cartão (o do código de barras no Wallet): baixar o cartão. */
  serialNumber: string;
  balance: number;
  profile: ParticipantProfile;
  program: ParticipantProgram;
  features: {
    mgm: boolean;
    surveyId: string | null;
    coupons: boolean;
  };
  /** Presente só quando o passbook tem `hasTiers`. */
  tier?: ParticipantTier | null;
}

export type OtpChannel = 'whatsapp' | 'email';

export interface OtpSent {
  /** `redirect`: WhatsApp — a pessoa manda a mensagem à Fixe e recebe o código na resposta. */
  status: 'sent' | 'redirect';
  channel: OtpChannel;
  /** Celular ou e-mail mascarado, para a tela dizer para onde foi. */
  destination: string;
  /** Conversa com o WhatsApp da Fixe com a mensagem pronta (só no `redirect`). */
  whatsappUrl?: string;
}

export type CouponStatus = 'ATRIBUIDO' | 'USADO' | 'EXPIRADO';

export interface ParticipantCoupon {
  code: string;
  status: CouponStatus;
  campaignName: string;
  benefit: string;
  assignedAt: string | null;
  expiresAt: string | null;
  usedAt: string | null;
  orderNo: string | null;
  redeemUrl: string | null;
}

export interface CouponEligibility {
  eligible: boolean;
  alreadyClaimed: boolean;
}

export interface CouponClaimResult {
  status: 'claimed' | 'already_claimed';
  code: string;
}

const base = (idPassbook: string) => `${PREFIX}/${encodeURIComponent(idPassbook)}`;

type Listener = () => void;
const expiredListeners = new Set<Listener>();

/** Sessão expirada no meio do uso (401 numa aba): a página volta para Entrar. */
export function onParticipantSessionExpired(listener: Listener): () => void {
  expiredListeners.add(listener);
  return () => {
    expiredListeners.delete(listener);
  };
}

async function member<T>(request: Promise<T>): Promise<T> {
  try {
    return await request;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) expiredListeners.forEach((listener) => listener());
    throw err;
  }
}

export const participantApi = {
  me: (idPassbook: string) => http.get<ParticipantMe>(`${base(idPassbook)}/me`),
  sendCode: (idPassbook: string, input: { cpf: string; phone: string; channel: OtpChannel }) =>
    http.post<OtpSent>(`${base(idPassbook)}/otp/send`, input),
  verifyCode: (idPassbook: string, input: { cpf: string; token: string }) =>
    http.post<ParticipantMe>(`${base(idPassbook)}/otp/verify`, input),
  logout: (idPassbook: string) => http.post<void>(`${base(idPassbook)}/logout`),
  saveProfile: (idPassbook: string, patch: Partial<ParticipantProfile>) =>
    member(http.put<{ profile: ParticipantProfile }>(`${base(idPassbook)}/me/profile`, patch)),
  history: (idPassbook: string, limit: number) =>
    member(http.get<{ logs: unknown[] }>(`${base(idPassbook)}/me/history?limit=${limit}`)),
  mgm: (idPassbook: string) => member(http.get<MgmParticipantView>(`${base(idPassbook)}/me/mgm`)),
  coupons: (idPassbook: string) =>
    member(http.get<{ coupons: ParticipantCoupon[] }>(`${base(idPassbook)}/me/coupons`)),
  couponEligibility: (idPassbook: string) =>
    member(http.get<CouponEligibility>(`${base(idPassbook)}/me/coupons/eligibility`)),
  claimCoupon: (idPassbook: string) => member(http.post<CouponClaimResult>(`${base(idPassbook)}/me/coupons/claim`)),
};
